/* eslint-disable max-classes-per-file */
import { QueryFormatterV2 } from "firebolt-sdk/build/src/formatter/formatter_v2";
import { JSONParser } from "firebolt-sdk/build/src/statement/stream/parser";
import camelCase from "lodash-es/camelCase";
import { QUERY_OUTPUT } from "types/outputFormat";
import { statusCodes } from "utils/constants";

import { MyAccount } from "services/account/account.types";
import { privateApi } from "services/api/private";

import { UnauthorizedError } from "components/ErrorBoundary/UnauthorizedError";

import { authService } from "../auth";
import { normalizeResponse } from "../query/helpers";
import JSONError from "./JsonError";
import {
  normalizeFullEngineURL,
  normalizeSystemEngineURL,
  throwErrorMessage,
} from "./helpers";

const RETRY_COUNT = 3;

type Columns<T> = { name: keyof T; type: string }[];

export type Result<T extends Record<string, unknown>> = {
  rows: T[];
  columns: Columns<T>;
};

type Parameter = unknown;

type Options<T> = {
  parameters?: Parameter[];
  transformRow?: (row: T, columns: Columns<T>) => typeof row;
  namedParameters?: Record<string, unknown>;
  database?: string;
  endpoint?: string;
  signal?: AbortSignal;
  accountName?: string;
  preserveColumnNames?: boolean;
};

export class SystemEngineEnvironment {
  apiEndpoint: string;

  apiEndpointMap: Record<string, string>;

  formatter: QueryFormatterV2;

  account_id?: string; // account context

  constructor() {
    this.apiEndpoint = "";
    this.apiEndpointMap = {};
    this.formatter = new QueryFormatterV2();
  }

  setAccountContext = (account_id: string) => {
    this.account_id = account_id;
  };

  resetAccountContext = () => {
    this.account_id = undefined;
  };

  initializeDefaultApiEndpoint = async () => {
    const url = `/engineUrl`;
    const response = await privateApi.get<{ engineUrl: string }>(url);
    const data = response.data;
    const { engineUrl } = data;
    this.apiEndpoint = `${engineUrl}`;
  };

  getApiEndpointByAccount = async (accountName: string) => {
    if (this.apiEndpointMap[accountName]) {
      return this.apiEndpointMap[accountName];
    }
    const url = `/account/${accountName}/engineUrl`;
    const response = await privateApi.get<{ engineUrl: string }>(url);
    const data = response.data;
    const { engineUrl } = data;
    const normalizedSystemEngineUrl = normalizeSystemEngineURL(engineUrl);
    return `${normalizedSystemEngineUrl}`;
  };

  initializeApiEndpoint = async (account: MyAccount) => {
    const endpoint = await this.getApiEndpointByAccount(account.accountName);
    this.apiEndpoint = endpoint;
    this.apiEndpointMap[account.accountName] = endpoint;
  };

  setApiEndpoint = (apiEndpoint: string) => {
    this.apiEndpoint = apiEndpoint;
  };

  getApiEndpoint = () => {
    return this.apiEndpoint;
  };

  getEndpoint = async <T>(options?: Options<T>) => {
    if (options?.accountName) {
      const apiEndpoint = await this.getApiEndpointByAccount(
        options.accountName
      );
      this.apiEndpointMap[options?.accountName] = apiEndpoint;
      return apiEndpoint;
    }
    return this.apiEndpoint;
  };

  buildRequestUrl = async <T>(options?: Options<T>) => {
    const searchParams = new URLSearchParams({
      output_format: QUERY_OUTPUT.JSON,
    });

    if (options?.database) {
      searchParams.append("database", options.database);
    }

    const queryString = searchParams ? `?${searchParams.toString()}` : "";
    const path = "/query";

    if (options?.endpoint) {
      const queryStringFormatted = options.endpoint.includes("?")
        ? queryString.replace("?", "&")
        : queryString;
      const endpoint = normalizeFullEngineURL(options.endpoint);
      return endpoint + queryStringFormatted;
    }
    const apiEndpoint = await this.getEndpoint<T>(options);
    return apiEndpoint + path + queryString;
  };

  execute = async <T extends Record<string, unknown>>(
    sql: string,
    options?: Options<T>
  ): Promise<Result<T>[]> => {
    const token = await authService.getToken();

    if (!token) {
      await authService.redirectToLogout();
      return [];
    }

    let retryCount = 0;
    const getResponse = async (token: string) => {
      const headers = {
        "Content-Type": "text/plain",
        Authorization: `Bearer ${token}`,
        "Firebolt-Machine-Query": "1",
      };

      const formated = this.formatter.formatQuery(
        sql,
        options?.parameters,
        options?.namedParameters
      );

      const requestOptions = {
        method: "POST",
        headers,
        body: formated,
        signal: options?.signal,
      };

      const url = await this.buildRequestUrl<T>(options);
      const response = await fetch(url, requestOptions);

      if (response.status === statusCodes.UNAUTHORIZED) {
        const text = await response.text();
        if (retryCount <= RETRY_COUNT) {
          retryCount += 1;
          const token = await authService.getToken();
          if (!token) {
            throw new UnauthorizedError(text);
          }
          return await getResponse(token);
        }
        throw new UnauthorizedError(text);
      }
      return response;
    };

    try {
      const response = await getResponse(token);
      const contentType = response.headers.get("content-type");

      if (response.status !== 200 && response.status !== 201) {
        try {
          const error = await response.clone().json();
          const errorMessage = error?.errors?.[0]?.description;
          throw new JSONError(errorMessage);
        } catch (e) {
          if (e instanceof JSONError) {
            return throwErrorMessage(response.status, e.message);
          }

          const text = await response.text();

          return throwErrorMessage(response.status, text);
        }
      }
      if (contentType && contentType.match(/text/)) {
        const error = await response.text();
        const message = "Query failed with reason: " + error;
        throw new Error(message);
      }

      if (contentType && contentType.match(/application\/json/)) {
        const body = await response.text();
        const parser = new JSONParser({});
        parser.processBody(body);
        const results = parser.results.map(result =>
          this.transformResult(result as Result<T>, options)
        );
        return results as Result<T>[];
      }
      return [];
    } catch (error) {
      console.log(error);
      throw error;
    }
  };

  transformResult = <T extends Record<string, unknown>>(
    result: Result<T>,
    options?: Options<T>
  ): Result<T> => {
    const { columns, rows, ...rest } = result;

    const transformedColumns = columns.map(column => {
      const name = options?.preserveColumnNames
        ? column.name
        : (camelCase(column.name as string) as keyof T);
      return { ...column, name };
    });

    const transformedRows = rows.map(row => {
      const transformedRow: Record<string, unknown> = {};

      for (const columnName in row) {
        const value = row[columnName];
        const name = options?.preserveColumnNames
          ? columnName
          : camelCase(columnName);
        transformedRow[name] = value;
      }

      return this.transformRow(
        transformedRow as T,
        transformedColumns,
        options
      );
    });

    return { columns: transformedColumns, rows: transformedRows, ...rest };
  };

  transformRow = <T extends Record<string, unknown>>(
    row: T,
    columns: Columns<T>,
    options?: Options<T>
  ) => {
    return options?.transformRow ? options.transformRow(row, columns) : row;
  };
}

export class LiteSystemEngineEnvironment extends SystemEngineEnvironment {
  constructor() {
    super();
    this.apiEndpoint = `${window.location.protocol}//${window.location.host}`;
  }

  buildRequestUrl = async <T>(options?: Options<T>) => {
    const searchParams = new URLSearchParams({
      output_format: QUERY_OUTPUT.JSON_COMPACT,
    });

    if (options?.database) {
      searchParams.append("database", options.database);
    }

    const queryString = searchParams ? `?${searchParams.toString()}` : "";
    const path = "/query";

    if (options?.endpoint) {
      return options.endpoint + queryString;
    }
    const apiEndpoint = await this.getEndpoint<T>(options);
    return apiEndpoint + path + queryString;
  };

  execute = async <T extends Record<string, unknown>>(
    sql: string,
    options?: Options<T>
  ): Promise<Result<T>[]> => {
    const headers = {
      "Content-Type": "application/json",
      "Firebolt-Machine-Query": "1",
    };

    const formated = this.formatter.formatQuery(
      sql,
      options?.parameters,
      options?.namedParameters
    );

    const requestOptions = {
      method: "POST",
      headers,
      body: formated,
      signal: options?.signal,
    };

    const url = await this.buildRequestUrl<T>(options);

    const response = await fetch(url, requestOptions);
    const body = await response.json();

    if (!response.ok) {
      const errorMessage = body?.errors?.[0]?.description;
      throw new JSONError(errorMessage || "Request failed");
    }

    const res = normalizeResponse({
      response: body,
      querySettings: {
        output_format: QUERY_OUTPUT.JSON_COMPACT,
      },
      responseStatusCode: response.status,
    });

    return [
      {
        rows: res.data.map(result =>
          Object.entries(result).reduce((acc, [key, value]) => {
            // @ts-ignore
            acc[camelCase(key)] = value;
            return acc;
          }, {})
        ) as T[],
        columns: res.meta,
      },
    ];
  };
}

export const systemEngineEnvironment = process.env.REACT_APP_LITE
  ? new LiteSystemEngineEnvironment()
  : new SystemEngineEnvironment();
